/* ==========================================================================
   IL | Notify drawer
   --------------------------------------------------------------------------
   NOT part of Prestige. Loaded from layout/theme.liquid.

   Opens the drawer from any [data-il-notify] trigger and posts to Klaviyo's
   CLIENT endpoints, which take the public key only. Klaviyo's own docs are
   explicit that these are for browsable pages and that a private key must
   never be used here. The public key is already in the page anyway, put there
   by the Klaviyo app embed.

   Two calls, and the second only when the box is ticked:

     /client/back-in-stock-subscriptions   always. Carries the VARIANT, which is
                                           the thing the flow waits on. No
                                           marketing consent is recorded.
     /client/subscriptions                 only on tick. Records consent against
                                           the newsletter list, with a source.

   Both answer 202 Accepted, not 200, and both are asynchronous: a 202 means
   Klaviyo took the request, not that the profile exists yet. So success here
   means "accepted", which is the honest thing to tell the customer.

   THE DRAWER ITSELF IS PRESTIGE'S. Since 2026-08-23 the markup is an <x-drawer>,
   so the overlay, Escape, the focus trap, returning focus to whatever opened it,
   the scroll lock, the slide animation and the close button all belong to the
   theme. What is left here is the only part that was ever ours: filling the
   drawer from the button that opened it, and talking to Klaviyo.

   Re-apply after a Prestige update: copy this file back and re-add the script
   tag in layout/theme.liquid. See reference/THEME-EDITS.md.
   ========================================================================== */
(() => {
  'use strict';

  const REVISION = '2026-07-15';           // Klaviyo pins its API by date, not semver.
  const BASE = 'https://a.klaviyo.com/client';

  // IL EDIT: lazy binding also supports replacement markup in the theme editor.
  const drawers = new WeakMap();
  function bind(root) {

  const formEl  = root.querySelector('[data-il-notify-form]');
  const doneEl  = root.querySelector('[data-il-notify-done]');
  const emailEl = root.querySelector('[data-il-notify-email]');
  const optinEl = root.querySelector('[data-il-notify-optin]');
  const errEl   = root.querySelector('[data-il-notify-error]');
  const submitEl= root.querySelector('[data-il-notify-submit]');
  const prodEl  = root.querySelector('[data-il-notify-prod]');
  const footEl  = root.querySelector('[data-il-notify-foot]');

  const KEY    = root.dataset.key;
  const LIST   = root.dataset.list;
  const SOURCE = root.dataset.source || 'Back in stock';

  // IL EDIT 2026-08-23. Whether the newsletter tick is compulsory, and what it starts as.
  // `defaultChecked` reads the `checked` attribute Liquid printed, so the merchant's setting is
  // the single source of truth and this file never decides the default itself.
  const OPTIN_REQUIRED = root.dataset.optinRequired === 'true';
  const OPTIN_DEFAULT  = optinEl ? optinEl.defaultChecked : false;

  let variant = null;
  let pending = false;
  let requestVersion = 0;

  /* ------------------------------------------------------------- open/close */

  function fill(trigger) {
    variant = trigger.dataset.ilVariant || null;
    const title = trigger.dataset.ilTitle;
    const meta  = trigger.dataset.ilMeta;
    const img   = trigger.dataset.ilImage;
    if (title) {
      root.querySelector('[data-il-notify-title]').textContent = title;
      root.querySelector('[data-il-notify-meta]').textContent = meta || '';
      const i = root.querySelector('[data-il-notify-img]');
      if (img) { i.src = img; i.hidden = false; } else { i.hidden = true; }
      prodEl.hidden = false;
    } else {
      prodEl.hidden = true;
    }
  }

  function open(trigger) {
    requestVersion += 1;
    pending = false;
    submitEl.disabled = false;
    submitEl.removeAttribute('aria-busy');
    emailEl.removeAttribute('aria-invalid');
    fill(trigger);
    // Always reopen on the form, never on somebody else's confirmation.
    formEl.hidden = false;
    doneEl.hidden = true;
    if (footEl) footEl.hidden = false;
    errEl.hidden = true;
    emailEl.value = '';
    // Back to the merchant's default, NOT to false. Hard coding false here would have silently
    // overridden a pre-ticked box on every open, which is exactly the kind of bug that looks like
    // the setting not working.
    if (optinEl) optinEl.checked = OPTIN_DEFAULT;
    // Reset before showing, so x-drawer's initial focus lands on a clean field. Its
    // initialFocus is the first input, which is exactly the email box we want focused.
    root.show();
  }

  /* ----------------------------------------------------------------- submit */

  function fail(message) {
    errEl.textContent = message;
    errEl.hidden = false;
    submitEl.disabled = false;
    submitEl.removeAttribute('aria-busy');
  }

  async function post(path, body) {
    const res = await fetch(`${BASE}/${path}/?company_id=${encodeURIComponent(KEY)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(body),
    });
    // 202 Accepted is the documented success. Anything else is a failure worth
    // showing, rather than a spinner that stops and says nothing.
    if (res.status !== 202) throw new Error(`${path} responded ${res.status}`);
  }

  async function submit() {
    if (pending) return;
    const email = (emailEl.value || '').trim();
    // Deliberately loose. Real address validation is Klaviyo's job; this only
    // catches the obvious typo before spending a network call on it.
    if (!email || !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(email)) {
      fail(root.dataset.errEmail || 'That email address does not look right.');
      emailEl.setAttribute('aria-invalid', 'true');
      emailEl.focus();
      return;
    }
    if (!variant || !KEY || (OPTIN_REQUIRED && (!LIST || !optinEl))) {
      fail(root.dataset.errGeneric); return;
    }

    // The tick is compulsory only because the Klaviyo flow is a marketing message and will not
    // deliver to an unsubscribed profile. See the drawer snippet for the full reasoning.
    if (OPTIN_REQUIRED && optinEl && !optinEl.checked) {
      fail(root.dataset.errOptin || 'Please tick the box so we are allowed to email you.');
      optinEl.focus();
      return;
    }

    errEl.hidden = true;
    emailEl.removeAttribute('aria-invalid');
    pending = true;
    const version = requestVersion;
    const requestedVariant = variant;
    const optedIn = Boolean(optinEl?.checked);
    submitEl.disabled = true;
    submitEl.setAttribute('aria-busy', 'true');

    try {
      await post('back-in-stock-subscriptions', {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: requestedVariant } },
          },
        },
      });

      // Only now, and only if asked. Kept as a separate call on purpose: the two are
      // different permissions and are recorded separately, even now that one is
      // compulsory in the form.
      if (optedIn && LIST) {
        try {
          await post('subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: SOURCE,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: LIST } } },
            },
          });
        } catch (e) {
          console.warn('[il-notify] newsletter opt in failed', e);
          // IL EDIT 2026-08-23. Swallowing this used to be right, because the notification did
          // not depend on it. It does now: while the Klaviyo flow is a marketing message, a
          // profile with no consent receives nothing, so a silent failure here would show
          // "We will let you know" to somebody who never hears from us. Retrying is safe,
          // Klaviyo deduplicates a repeat back in stock subscription for the same variant.
          if (OPTIN_REQUIRED) throw e;
        }
      }

      if (version !== requestVersion || !root.isConnected) return;
      formEl.hidden = true;
      doneEl.hidden = false;
      // The tick and the fine print go with the form. Leaving a ticked box on the
      // confirmation offers a choice that has already been made, and leaving the
      // disclaimer explains a form that is no longer there.
      if (footEl) footEl.hidden = true;
      doneEl.focus();
    } catch (e) {
      if (version !== requestVersion || !root.isConnected) return;
      console.warn('[il-notify]', e);
      fail(root.dataset.errGeneric || 'We could not save that just now. Please try again.');
    } finally {
      if (version === requestVersion) {
        pending = false;
        submitEl.disabled = false;
        submitEl.removeAttribute('aria-busy');
      }
    }
  }

  /* ------------------------------------------------------------------- wire */

  /* Not `aria-controls`, which is how the theme's own triggers open a drawer, because
     the drawer has to be filled from the trigger before it is shown: one drawer serves
     every product on the page. So the click is handled here and `show()` is called once
     the contents are right. Closing needs no wiring at all: the overlay, the close button
     and Escape are all Prestige's. */
  submitEl.addEventListener('click', submit);
  emailEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
  });
  return open;
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-il-notify]');
    if (!trigger) return;
    const root = document.querySelector('#il-notify-drawer');
    if (!root) return;
    e.preventDefault();
    if (!drawers.has(root)) drawers.set(root, bind(root));
    drawers.get(root)(trigger);
  });

})();
